import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Circle, RotateCcw, CloudUpload, CloudOff, Cloud } from 'lucide-react';

/* ======================================================
   Response capture
   Clickable questions placed on slides. Every change is:
     1. kept in the browser (localStorage), so a refresh loses nothing
     2. saved behind the scenes to `saveUrl` (debounced), as one
        snapshot per session: { deck, session, answers, updatedAt }
====================================================== */

const ResponsesContext = createContext(null);

const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch (e) {
    return fallback;
  }
};
const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};
const newSessionId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const ResponsesProvider = ({ storageKey, deck, questions, saveUrl, children }) => {
  const key = `${storageKey}-responses`;
  const sessionKey = `${storageKey}-session`;
  const [answers, setAnswers] = useState(() => read(key, {}));
  const [session] = useState(() => {
    const existing = read(sessionKey, null);
    if (existing) return existing;
    const id = newSessionId();
    write(sessionKey, id);
    return id;
  });
  const [saveState, setSaveState] = useState(saveUrl ? 'idle' : 'local'); // idle | saving | saved | error | local
  const first = useRef(true);
  const latest = useRef(null);

  const payload = useCallback(
    (a) => ({
      deck,
      session,
      updatedAt: new Date().toISOString(),
      answers: questions.map((q) => ({ id: q.id, question: q.short || q.question, answer: a[q.id] || [] })),
    }),
    [deck, session, questions]
  );

  // Keep a local copy on every change
  useEffect(() => write(key, answers), [key, answers]);

  // Save behind the scenes, ~1s after the last click
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return undefined;
    }
    latest.current = payload(answers);
    if (!saveUrl) return undefined;
    setSaveState('saving');
    const t = setTimeout(async () => {
      try {
        const res = await fetch(saveUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(latest.current),
          keepalive: true,
        });
        if (!res.ok) throw new Error(String(res.status));
        setSaveState('saved');
      } catch (e) {
        setSaveState('error');
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [answers, payload, saveUrl]);

  // If the tab is closed or hidden mid-save, send the latest snapshot anyway
  useEffect(() => {
    if (!saveUrl) return undefined;
    const flush = () => {
      if (document.visibilityState === 'hidden' && latest.current && navigator.sendBeacon) {
        navigator.sendBeacon(saveUrl, new Blob([JSON.stringify(latest.current)], { type: 'application/json' }));
      }
    };
    document.addEventListener('visibilitychange', flush);
    return () => document.removeEventListener('visibilitychange', flush);
  }, [saveUrl]);

  const toggle = useCallback(
    (qid, value) => {
      const q = questions.find((x) => x.id === qid);
      setAnswers((prev) => {
        const current = prev[qid] || [];
        let next;
        if (q?.multi) next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        else next = current[0] === value ? [] : [value];
        return { ...prev, [qid]: next };
      });
    },
    [questions]
  );

  const value = useMemo(
    () => ({ answers, toggle, questions, setAnswers, saveState }),
    [answers, toggle, questions, saveState]
  );
  return <ResponsesContext.Provider value={value}>{children}</ResponsesContext.Provider>;
};

/* Small status shown in the control bar and on the summary slide */
export const SaveStatus = () => {
  const { saveState } = useResponses();
  const map = {
    idle: [Cloud, 'Saves as you click'],
    saving: [CloudUpload, 'Saving…'],
    saved: [CheckCircle2, 'Saved'],
    error: [CloudOff, 'Saved on this device — will retry'],
    local: [CheckCircle2, 'Saved on this device'],
  };
  const [Ic, text] = map[saveState] || map.idle;
  return (
    <span className={`saveStatus ${saveState}`}>
      <Ic size={14} /> {text}
    </span>
  );
};

export const useResponses = () => useContext(ResponsesContext);

/* Current answer(s) for one question */
export const useAnswer = (id) => {
  const { answers, toggle, questions } = useResponses();
  return { selected: answers[id] || [], toggle: (v) => toggle(id, v), question: questions.find((q) => q.id === id) };
};

/* ---------- Logo ----------
   `logos` maps a name to a website domain (icon via Google's favicon
   service) or a full image URL. Falls back to the name alone. */
const logoSrc = (v) => (v.startsWith('http') ? v : `https://www.google.com/s2/favicons?domain=${v}&sz=128`);

export const LogoSlot = ({ name, logos = {}, size = 36, showName = true }) => {
  const [failed, setFailed] = useState(false);
  const src = logos[name];
  return (
    <span className="logoSlot">
      {src && !failed && (
        <img
          src={logoSrc(src)}
          alt={showName ? '' : name}
          className="logoImg"
          style={{ width: size, height: size }}
          onError={() => setFailed(true)}
        />
      )}
      {showName && <span className="logoText">{name}</span>}
    </span>
  );
};

/* ---------- Focus choice ----------
   Pick one option from a tile grid. Once picked, the grid collapses
   to a single chip and the slide shows `children(selected)`. */
export const FocusChoice = ({ id, logos, prompt, columns = 5, actions, children }) => {
  const { selected, toggle, question } = useAnswer(id);
  const current = selected[0];
  if (!question) return null;

  if (!current) {
    return (
      <div className="focusPick">
        <div className="choiceQ"><span>{question.question}</span><span className="choiceHint">{prompt || 'Select one'}</span></div>
        <div className="tiles" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {question.options.map((opt) => (
            <button type="button" key={opt} className="tile" onClick={() => toggle(opt)}>
              <LogoSlot name={opt} logos={logos} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="focusOpen">
      <div className="focusBar">
        <span className="focusLabel">{question.short || question.question}</span>
        <span className="tile on mini"><CheckCircle2 size={22} /><LogoSlot name={current} logos={logos} size={26} /></span>
        <button type="button" className="changeBtn" onClick={() => toggle(current)}>
          <RotateCcw size={18} /> Change
        </button>
        {actions && <div style={{ marginLeft: 'auto' }}>{actions}</div>}
      </div>
      {children(current)}
    </div>
  );
};

/* ---------- Segmented toggle (single choice, compact) ---------- */
export const Segmented = ({ id, label }) => {
  const { selected, toggle, question } = useAnswer(id);
  if (!question) return null;
  return (
    <div className="segWrap">
      {label && <span className="focusLabel">{label}</span>}
      <div className="segmented" role="group" aria-label={question.question}>
        {question.options.map((opt) => (
          <button type="button" key={opt} className={selected[0] === opt ? 'on' : ''} aria-pressed={selected[0] === opt} onClick={() => toggle(opt)}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ---------- A clickable question ---------- */

export const Choice = ({ id, columns, compact, logos }) => {
  const { answers, toggle, questions } = useResponses();
  const q = questions.find((x) => x.id === id);
  if (!q) return null;
  const selected = answers[id] || [];

  return (
    <div className={`choice ${compact ? 'compact' : ''}`}>
      <div className="choiceQ">
        <span>{q.question}</span>
        <span className="choiceHint">{q.multi ? 'Select all that apply' : 'Select one'}</span>
      </div>
      <div
        className="choiceOpts"
        style={columns ? { display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
      >
        {q.options.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              type="button"
              key={opt}
              className={`opt ${on ? 'on' : ''}`}
              aria-pressed={on}
              onClick={() => toggle(id, opt)}
            >
              {on ? <CheckCircle2 size={22} /> : <Circle size={22} />}
              {logos ? <LogoSlot name={opt} logos={logos} size={22} /> : opt}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ---------- Clickable card (for selecting whole items, e.g. priorities) ---------- */

export const useSelectable = (id, value) => {
  const { answers, toggle } = useResponses();
  const on = (answers[id] || []).includes(value);
  return {
    on,
    props: {
      role: 'button',
      tabIndex: 0,
      'aria-pressed': on,
      onClick: () => toggle(id, value),
      onKeyDown: (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          toggle(id, value);
        }
      },
    },
  };
};

/* ---------- Summary (everything captured so far) ---------- */

export const ResponsesSummary = () => {
  const { answers, questions, setAnswers } = useResponses();
  const answered = questions.filter((q) => (answers[q.id] || []).length);
  const open = questions.filter((q) => !(answers[q.id] || []).length);

  return (
    <div className="summaryList full">
      {answered.length === 0 && (
        <div className="summaryRow" style={{ gridColumn: '1 / -1' }}>
          <span className="summaryA empty">Nothing captured yet — answers appear here as options are clicked.</span>
        </div>
      )}
      {answered.map((q) => (
        <div className="summaryRow" key={q.id}>
          <span className="summaryQ">{q.short || q.question}</span>
          <span className="summaryA">{answers[q.id].join(', ')}</span>
        </div>
      ))}
      {open.length > 0 && (
        <div className="summaryRow summaryOpen" style={{ gridColumn: '1 / -1' }}>
          <span className="summaryQ">To discuss · {open.length} more</span>
          <span className="summaryOpenList">{open.map((q) => q.short || q.question).join(' · ')}</span>
        </div>
      )}
      <div className="summaryMeta">
        <span>
          {answered.length} of {questions.length} answered · <SaveStatus />
        </span>
        {answered.length > 0 && (
          <button type="button" className="linkBtn" onClick={() => setAnswers({})}>
            Clear answers
          </button>
        )}
      </div>
    </div>
  );
};
